import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import { Icon, type IconName } from "../Icon/Icon";
import { busyAria } from "../a11y";
import { webTapFloor, type UiTheme } from "../theme";

// THE shared button. Collapses the ~20 hand-rolled `brand500 + inkOnBrand`
// filled-button silos (next/consolidation.md) into one control: size, padding,
// radius, hitSlop and the four variants encoded ONCE. Filled ~40 tall with a
// hitSlop of 8 → ≥44 touch on native. react-native-web's Pressable doesn't
// implement hitSlop at all (DS-48), so both heights below also carry
// `webTapFloor` — a no-op on native, a floor to 44 on web.
//
// `variant`: primary (brand fill) · secondary (outline) · danger (outline,
// destructive) · ghost (text only). The caller owns layout (flex/margins/
// alignSelf/`full`) via `style`; the primitive owns the box internals.

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

export type ButtonProps = {
  theme: UiTheme;
  label: string;
  onPress: () => void;
  variant?: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
  /** Optional leading icon (reuses the shared <Icon>). */
  icon?: IconName;
  /** Stretch to fill the cross axis. */
  full?: boolean;
  /** A denser box (~32 tall, sm label) for chrome actions like the top-bar
   *  Create, where a full ~40 button dwarfs a text brand. Touch stays ≥44 via
   *  hitSlop — the visual box shrinks, not the tap target. */
  compact?: boolean;
  /**
   * Touch expansion. Defaults to 8 (→ ≥44 touch). Pass `{top,bottom}` for
   * adjacent buttons in a row so horizontal slop doesn't overlap the sibling.
   */
  hitSlop?: PressableProps["hitSlop"];
  /** Layout-only override (flex/margins/alignSelf). Merged last. */
  style?: StyleProp<ViewStyle>;
  /** Wrap the label to at most this many lines, centred, for a button
   *  narrower than its longest translation. Absent: no limit. */
  numberOfLines?: number;
  accessibilityLabel?: string;
  /** Extra spoken context (no visual effect, unchanged tap) — the same slot
   *  <IconButton> already had. Its first use is the reason a DISABLED button
   *  is disabled: a sighted reader infers it from the greyed box beside an
   *  untouched form, and a screen-reader user was hearing only "Save, dimmed"
   *  (a11y audit, 2026-08-17). Parity gap between the two button primitives,
   *  not a new concept. */
  accessibilityHint?: string;
};

export function Button({
  theme,
  label,
  onPress,
  variant = "primary",
  loading = false,
  disabled = false,
  icon,
  full = false,
  compact = false,
  hitSlop = 8,
  style,
  numberOfLines,
  accessibilityLabel,
  accessibilityHint,
}: ButtonProps) {
  const styles = makeStyles(theme);
  const blocked = disabled || loading;
  // Disabled primary drops to a surface2 fill, so its label must switch to ink3
  // (AA on surface2) — inkOnBrand would be invisible on the grey.
  const textColor =
    disabled && variant === "primary"
      ? theme.palette.ink3
      : TEXT_COLOR[variant](theme);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled: blocked, busy: loading }}
      // ⚠️ The line above is inert on web — RNW drops `accessibilityState`, so
      // while a submit was in flight the label became a spinner and a screen
      // reader heard the button go silent, not busy. `disabled` is NOT affected
      // (RNW's own Pressable emits `aria-disabled` from the `disabled` prop),
      // so `busy` was the only half really lost. Why `aria-*` and not a
      // platform branch: see `busyAria`. RW-38.
      {...busyAria(loading)}
      disabled={blocked}
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ pressed }) => [
        styles.base,
        compact && styles.compact,
        styles[variant],
        full && styles.full,
        pressed && !blocked && styles[`${variant}Pressed` as const],
        // Loading keeps the normal fill (spinner rides on it); only a true
        // `disabled` mutes the box.
        disabled && (variant === "primary" ? styles.primaryDisabled : styles.disabled),
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={textColor} />
      ) : (
        <View style={styles.content}>
          {icon ? <Icon name={icon} size={compact ? 16 : 18} color={textColor} /> : null}
          <Text
            style={[
              styles.label,
              compact && styles.labelCompact,
              numberOfLines !== undefined && styles.labelWrap,
              { color: textColor },
            ]}
            numberOfLines={numberOfLines}
          >
            {label}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

// Text/glyph colour per variant — also fed to the spinner and the icon so the
// three always agree.
const TEXT_COLOR: Record<ButtonVariant, (t: UiTheme) => string> = {
  primary: (t) => t.palette.inkOnBrand,
  secondary: (t) => t.palette.ink,
  danger: (t) => t.palette.danger,
  ghost: (t) => t.palette.ink2,
};

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    base: {
      minHeight: webTapFloor ?? 40,
      paddingHorizontal: s.s4,
      borderRadius: r.r3,
      alignItems: "center",
      justifyContent: "center",
    },
    compact: { minHeight: webTapFloor ?? 32, paddingHorizontal: s.s3 },
    content: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    label: { fontSize: t.sizes.base, fontWeight: "700" },
    labelCompact: { fontSize: t.sizes.sm },
    // Shrinks beside the icon so the label can wrap inside the box.
    labelWrap: { flexShrink: 1, textAlign: "center" },
    full: { alignSelf: "stretch" },

    primary: { backgroundColor: p.brand500 },
    primaryPressed: { backgroundColor: p.brand600 },
    primaryDisabled: { backgroundColor: p.surface2 },

    secondary: { borderWidth: 1, borderColor: p.border },
    secondaryPressed: { backgroundColor: p.surface2 },

    danger: { borderWidth: 1, borderColor: p.danger },
    dangerPressed: { backgroundColor: p.dangerFill },

    ghost: {},
    ghostPressed: { opacity: 0.6 },

    disabled: { opacity: 0.5 },
  });
}
